import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';
import ConfirmActionModal from '../component/ConfirmActionModal';

const DEFAULT_LOGO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

const tk = (n) => `Tk ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
// Same rule as the server: more than 0, at most two decimals.
const AMOUNT_RX = /^\d+(\.\d{1,2})?$/;

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_CHIP = {
  pending: { background: '#fef3c7', color: '#92400e', label: 'Pending' },
  processing: { background: '#fef3c7', color: '#92400e', label: 'Processing' },
  approved: { background: '#dcfce7', color: '#166534', label: 'Approved' },
  rejected: { background: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  cancelled: { background: '#f1f5f9', color: '#475569', label: 'Cancelled' },
};

/**
 * REQUEST MONEY — the member's side.
 *
 * Pay a merchant above you outside the app (cash, bKash), then ask them here
 * for the same amount of Shopping Balance. Only merchants in your own upline
 * are offered; the merchant approves (the balance moves from them to you) or
 * rejects with a reason. Below the form: your own requests, with cancel while
 * pending and a receipt PDF once approved.
 */
function Frame({ onBack, children }) {
  return (
    <div>
      <Helmet>
        <title>Request Money</title>
      </Helmet>
      <style>{CSS}</style>
      <Container className="no-gutter">
        <div className="card mb-3">
          <Row>
            <Col style={{ textAlign: 'center' }}>
              <img src={DEFAULT_LOGO} style={{ width: '15rem', margin: '2rem' }} alt="ClickCart Logo" />
            </Col>
          </Row>
          <div className="card-header" onClick={onBack} role="button">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i> {'  '} Request Money
          </div>
          <div className="card-body rm-body">{children}</div>
        </div>
      </Container>
    </div>
  );
}

export default function RequestMoneyScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const auth = useMemo(() => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }), [userInfo]);

  /* ------------------------------------------------------------ merchants */

  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [merchantId, setMerchantId] = useState('');
  const [amount, setAmount] = useState('');
  const [trxId, setTrxId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const loadMerchants = useCallback(async () => {
    setMerchantsLoading(true);
    try {
      const { data } = await Axios.get(api + '/api/money-requests/merchants', auth);
      const rows = data.rows || [];
      setMerchants(rows);
      // One choice only: pick it for them.
      if (rows.length === 1) setMerchantId(String(rows[0]._id));
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setMerchantsLoading(false);
    }
  }, [auth]);

  /* ---------------------------------------------------------- my requests */

  const [mine, setMine] = useState({ rows: [], page: 1, pages: 1, count: 0, pendingCount: 0, maxOpen: 5 });
  const [mineStatus, setMineStatus] = useState('');
  const [mineLoading, setMineLoading] = useState(false);

  const loadMine = useCallback(
    async (page = 1, status = mineStatus) => {
      setMineLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize: 10 });
        if (status) params.set('status', status);
        const { data } = await Axios.get(api + `/api/money-requests/mine?${params.toString()}`, auth);
        setMine(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setMineLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth]
  );

  useEffect(() => {
    if (!userInfo) {
      navigate('/signin?redirect=/request-money');
      return;
    }
    loadMerchants();
    loadMine(1, '');
  }, [userInfo, navigate, loadMerchants, loadMine]);

  const selected = merchants.find((m) => String(m._id) === merchantId) || null;

  const review = () => {
    if (!selected) return toast.error('Choose the merchant you paid.');
    const raw = amount.trim();
    if (!AMOUNT_RX.test(raw) || Number(raw) <= 0) {
      return toast.error('Enter an amount in Tk greater than 0, with at most two decimals.');
    }
    if (mine.pendingCount >= (mine.maxOpen || 5)) {
      return toast.error(`You already have ${mine.pendingCount} requests waiting. Wait for an answer or cancel one first.`);
    }
    setConfirm({ kind: 'submit', merchant: selected, amount: Number(raw), trxId: trxId.trim(), note: note.trim() });
  };

  const doSubmit = async ({ merchant, amount: value, trxId: trx, note: text }) => {
    setSubmitting(true);
    try {
      const { data } = await Axios.post(
        api + '/api/money-requests',
        { merchantId: merchant._id, amountBdt: value, trxId: trx, note: text },
        auth
      );
      toast.success(data.message || 'Request sent.');
      setConfirm(null);
      setAmount('');
      setTrxId('');
      setNote('');
      setMineStatus('');
      loadMine(1, '');
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async (row) => {
    try {
      const { data } = await Axios.post(api + `/api/money-requests/${row._id}/cancel`, {}, auth);
      toast.success(data.message || 'Request cancelled.');
      setConfirm(null);
      loadMine(mine.page, mineStatus);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const pickStatus = (status) => {
    setMineStatus(status);
    loadMine(1, status);
  };

  const receiptUrl = (id) => `${api}/api/money-requests/${id}/receipt.pdf?token=${userInfo?.token}&download=1`;

  const goBack = () => navigate('/user-panel');
  const rows = mine.rows || [];

  return (
    <Frame onBack={goBack}>
      <p className="rm-intro">
        Paid a merchant above you in cash or bKash? Ask them here for the same amount of Shopping
        Balance. When they approve, it moves from their Shopping Balance to yours.
      </p>

      {/* ------------------------------------------------------------ form */}
      <section className="rm-panel">
        <h6 className="rm-panel-head">
          <i className="fa fa-hand-o-up" aria-hidden="true"></i> New request
        </h6>

        <label className="rm-label">Merchant you paid</label>
        {merchantsLoading ? (
          <div className="rm-muted">Loading merchants…</div>
        ) : merchants.length === 0 ? (
          <div className="rm-empty">
            No merchant in your upline yet. Request Money opens once someone above you in the tree is a merchant.
          </div>
        ) : (
          <div className="rm-merchants">
            {merchants.map((m) => {
              const on = String(m._id) === merchantId;
              return (
                <button
                  type="button"
                  key={m._id}
                  className={on ? 'rm-merchant rm-merchant-on' : 'rm-merchant'}
                  onClick={() => setMerchantId(String(m._id))}
                >
                  <img src={m.associationLogo || DEFAULT_LOGO} alt="" className="rm-logo" />
                  <span className="rm-merchant-text">
                    <b>{m.associationName || m.name || m.username}</b>
                    <small>
                      {m.name ? `${m.name} · ` : ''}@{m.username}
                      {m.phone ? ` · ${m.phone}` : ''}
                    </small>
                  </span>
                  {on ? <i className="fa fa-check-circle rm-tick" aria-hidden="true"></i> : null}
                </button>
              );
            })}
          </div>
        )}

        <label className="rm-label" htmlFor="rm-amount">Amount (Tk)</label>
        <input
          id="rm-amount"
          className="rm-input"
          inputMode="decimal"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
        />
        <label className="rm-label" htmlFor="rm-trx">Transaction ID (optional)</label>
        <input
          id="rm-trx"
          className="rm-input"
          placeholder="bKash / bank reference"
          maxLength={60}
          value={trxId}
          onChange={(e) => setTrxId(e.target.value)}
        />
        <label className="rm-label" htmlFor="rm-note">Note (optional)</label>
        <textarea
          id="rm-note"
          className="rm-input"
          rows={2}
          maxLength={300}
          placeholder="e.g. Paid cash at the shop on Monday"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="button"
          className="rm-btn rm-btn-primary"
          disabled={submitting || merchants.length === 0}
          onClick={review}
        >
          Review request
        </button>
        <div className="rm-muted">
          Up to {mine.maxOpen || 5} requests can wait at once. You have {mine.pendingCount || 0} pending.
        </div>
      </section>

      {/* ----------------------------------------------------- my requests */}
      <section className="rm-panel">
        <h6 className="rm-panel-head">
          <i className="fa fa-list" aria-hidden="true"></i> My money requests
          <span className="rm-count">{mine.count}</span>
        </h6>
        <div className="rm-tabs">
          {STATUS_TABS.map((t) => (
            <button
              type="button"
              key={t.value || 'all'}
              className={mineStatus === t.value ? 'rm-tab rm-tab-active' : 'rm-tab'}
              onClick={() => pickStatus(t.value)}
            >
              {t.label}
              {t.value === 'pending' ? <span className="rm-count">{mine.pendingCount || 0}</span> : null}
            </button>
          ))}
        </div>

        {mineLoading && rows.length === 0 ? (
          <div className="rm-muted rm-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rm-muted rm-center">No money requests yet.</div>
        ) : (
          <div className="rm-list">
            {rows.map((r) => {
              const chip = STATUS_CHIP[r.status] || STATUS_CHIP.pending;
              return (
                <article key={r._id} className="rm-card">
                  <img src={r.associationLogo || DEFAULT_LOGO} alt="" className="rm-logo" />
                  <div className="rm-card-info">
                    <b>{r.associationName || r.merchantName || r.merchantUsername}</b>
                    <small>
                      {r.serialText} · @{r.merchantUsername} · {new Date(r.createdAt).toLocaleString()}
                    </small>
                    {r.trxId ? <small>Trx ID: {r.trxId}</small> : null}
                    {r.note ? <small>Note: {r.note}</small> : null}
                    {r.status === 'rejected' && r.reason ? (
                      <small className="rm-reason">Reason: {r.reason}</small>
                    ) : null}
                    {r.status === 'approved' && r.approvedAt ? (
                      <small>Approved {new Date(r.approvedAt).toLocaleString()}</small>
                    ) : null}
                  </div>
                  <div className="rm-card-side">
                    <div className="rm-amount">{tk(r.amountBdt)}</div>
                    <span className="rm-chip" style={{ background: chip.background, color: chip.color }}>
                      {chip.label}
                    </span>
                    <div className="rm-actions">
                      {r.status === 'approved' ? (
                        <a className="rm-linkbtn" href={receiptUrl(r._id)} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      ) : null}
                      {r.status === 'pending' ? (
                        <button
                          type="button"
                          className="rm-linkbtn rm-linkbtn-danger"
                          onClick={() => setConfirm({ kind: 'cancel', row: r })}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {mine.pages > 1 ? (
          <div className="rm-pager">
            <button className="rm-btn" disabled={mine.page <= 1} onClick={() => loadMine(mine.page - 1, mineStatus)}>
              ‹ Prev
            </button>
            <span className="rm-muted">
              Page {mine.page} of {mine.pages}
            </span>
            <button
              className="rm-btn"
              disabled={mine.page >= mine.pages}
              onClick={() => loadMine(mine.page + 1, mineStatus)}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </section>

      {confirm?.kind === 'submit' ? (
        <ConfirmActionModal
          show
          title="Send money request"
          recipient={{
            name: confirm.merchant.associationName || confirm.merchant.name,
            username: confirm.merchant.username,
            photo: confirm.merchant.associationLogo,
          }}
          amount={tk(confirm.amount)}
          amountHint="Asked from this merchant's Shopping Balance"
          rows={[
            { label: 'Merchant', value: `${confirm.merchant.associationName || confirm.merchant.name} (@${confirm.merchant.username})` },
            { label: 'Amount', value: tk(confirm.amount) },
            ...(confirm.trxId ? [{ label: 'Transaction ID', value: confirm.trxId }] : []),
            ...(confirm.note ? [{ label: 'Note', value: confirm.note }] : []),
          ]}
          note={
            `Only send this after you have paid ${confirm.merchant.associationName || confirm.merchant.username} ` +
            `${tk(confirm.amount)}. Nothing moves until they approve; you can cancel while it is pending.`
          }
          confirmLabel="Send request"
          busyLabel="Sending…"
          busy={submitting}
          onCancel={() => setConfirm(null)}
          onConfirm={() => doSubmit(confirm)}
        />
      ) : null}
      {confirm?.kind === 'cancel' ? (
        <ConfirmActionModal
          show
          danger
          title="Cancel money request"
          amount={tk(confirm.row.amountBdt)}
          amountHint={`${confirm.row.serialText} to ${confirm.row.associationName || confirm.row.merchantUsername}`}
          rows={[
            { label: 'Merchant', value: `${confirm.row.associationName || confirm.row.merchantName} (@${confirm.row.merchantUsername})` },
            { label: 'Amount', value: tk(confirm.row.amountBdt) },
            { label: 'Sent', value: new Date(confirm.row.createdAt).toLocaleString() },
          ]}
          note="The merchant will no longer be able to approve it. Nothing has moved, so no balance changes."
          confirmLabel="Cancel request"
          busyLabel="Cancelling…"
          onCancel={() => setConfirm(null)}
          onConfirm={() => doCancel(confirm.row)}
        />
      ) : null}
    </Frame>
  );
}

const CSS = `
.rm-body { padding: 12px; display: flex; flex-direction: column; gap: 12px; }
.rm-intro { margin: 0; color: #14506e; font-size: 13.5px; line-height: 1.5; }
.rm-panel { background: #fff; border-radius: 14px; padding: 14px; display: flex;
  flex-direction: column; gap: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.rm-panel-head { display: flex; align-items: center; gap: 8px; margin: 0 0 4px; padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb; font-size: 13px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .03em; color: #14506e; }
.rm-panel-head i { color: #42b4e7; }
.rm-count { margin-left: auto; background: #e6f5fd; color: #14506e; border-radius: 999px;
  padding: 1px 9px; font-size: 12px; }
.rm-label { font-size: 12px; font-weight: 700; color: #374151; margin: 4px 0 -2px; }
.rm-input { width: 100%; border: 1px solid #cfd8e3; border-radius: 10px; padding: 10px 12px;
  font-size: 14px; box-sizing: border-box; background: #fff; }
.rm-input:focus { outline: 3px solid #cdeafa; border-color: #42b4e7; }
.rm-merchants { display: flex; flex-direction: column; gap: 6px; }
.rm-merchant { display: flex; align-items: center; gap: 10px; text-align: left; width: 100%;
  border: 1.5px solid #e5e7eb; background: #fff; border-radius: 12px; padding: 9px 12px; cursor: pointer; }
.rm-merchant:hover { border-color: #bcd8ea; }
.rm-merchant-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.rm-merchant-text { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: 1.3; }
.rm-merchant-text b { font-size: 14px; color: #111827; overflow-wrap: anywhere; }
.rm-merchant-text small { font-size: 12px; color: #6b7280; overflow-wrap: anywhere; }
.rm-tick { color: #16a34a; font-size: 20px; }
.rm-logo { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; background: #f1f5f9; flex-shrink: 0; }
.rm-empty { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 10px;
  padding: 9px 11px; font-size: 13px; }
.rm-btn { border-radius: 10px; padding: 10px 14px; font-weight: 700; font-size: 14px;
  cursor: pointer; border: 1px solid #cfd8e3; background: #f1f5f9; white-space: nowrap; }
.rm-btn:disabled { opacity: .6; cursor: not-allowed; }
.rm-btn-primary { background: #42b4e7; border-color: #42b4e7; color: #fff; margin-top: 6px; }
.rm-btn-primary:hover:enabled { background: #2f9fd1; }
.rm-muted { color: #6b7280; font-size: 12px; }
.rm-center { text-align: center; padding: 16px 0; }
.rm-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.rm-tab { display: inline-flex; align-items: center; gap: 7px; border: 1.5px solid #e5e7eb;
  background: #f8fafc; color: #374151; border-radius: 999px; padding: 6px 13px;
  font-size: 12.5px; font-weight: 700; cursor: pointer; }
.rm-tab .rm-count { margin-left: 2px; }
.rm-tab-active { background: linear-gradient(135deg, #42b4e7, #2f8fc4); border-color: #2f8fc4; color: #fff; }
.rm-tab-active .rm-count { background: rgba(255,255,255,.25); color: #fff; }
.rm-list { display: flex; flex-direction: column; gap: 8px; }
.rm-card { display: flex; align-items: flex-start; gap: 10px; border: 1.5px solid #e5e7eb;
  border-radius: 12px; padding: 10px 12px; flex-wrap: wrap; }
.rm-card-info { display: flex; flex-direction: column; min-width: 0; flex: 1 1 170px; line-height: 1.35; }
.rm-card-info b { font-size: 14px; color: #111827; }
.rm-card-info small { font-size: 12px; color: #6b7280; overflow-wrap: anywhere; }
.rm-reason { color: #991b1b !important; }
.rm-card-side { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; margin-left: auto; }
.rm-amount { font-size: 17px; font-weight: 800; color: #166534; }
.rm-chip { border-radius: 999px; padding: 3px 10px; font-size: 11px; font-weight: 700; }
.rm-actions { display: flex; gap: 6px; }
.rm-linkbtn { background: none; border: 0; color: #2f8fc4; font-size: 12.5px; font-weight: 700;
  cursor: pointer; padding: 2px 6px; text-decoration: none; }
.rm-linkbtn-danger { color: #b91c1c; }
.rm-pager { display: flex; align-items: center; justify-content: center; gap: 10px; padding-top: 10px; }
`;
